/* eslint-disable func-style */
/* eslint-disable @typescript-eslint/no-empty-function, no-empty-function */
/* eslint-disable no-unused-expressions */
import { isBrowser } from '@cp/utils/browser';
import { getDeviceId, tokens } from '@cp/shared/deviceId';
import { init } from '@sentry/browser';
import { PersistConfig } from 'redux-persist';
import { MODAL_MANAGER } from '@cp/shared/modal';
import { ROUTER } from '@cp/shared/router';
import { createBrowserHistory, createMemoryHistory } from 'history';
import { CachedJSONStorage, MemoryStorage } from '@cp/shared/storage';
import { CONFIG_MANAGER_TOKEN, createConfig } from 'src/app/providers/config';
import { SPIDER_SERVICE_TOKEN } from 'src/app/providers/tracking/const';
import { SpiderService } from 'src/app/providers/tracking/spiderService';
import { Container } from 'src/app/ioc';
import { RootState, STORE, configureStore } from 'src/app/redux';
import { RefModalManager } from 'src/app/providers/modal';
import { BrowserRouter, HISTORY_API, StaticRouter } from 'src/app/providers/router';
import { ClientSelectionService, SELECTION_SERVICE, SELECTION_STORAGE, SelectionWithPriority } from 'src/features/clientSelection';
import { CLIENT_SELECTION_GETTER, DEVICE_IDENTIFIER } from 'src/features/auth/di';
import { GET_CLIENT_SELECTIONS } from 'src/features/registration/di';
import { EVENT_BUS_TOKEN, EventBus } from './providers/eventBus';
import { registerServiceWorker } from './sw';
import { BrowserDeviceIDStorage, fingerprint, stubDIDStorage, uuidGenerator } from './providers/deviceId';

export function bootstrap(
  container: Container,
  {
    config,
    redux,
  }: {
    config?: Record<string, unknown>;
    redux: { preloadedState?: Partial<RootState>; persistConfig: PersistConfig<RootState> };
  },
) {
  console.groupCollapsed('bootstrap');
  const configManager = createConfig(config);
  const eventBus = isBrowser ? new EventBus() : { subscribe: () => {} };
  const spiderService = isBrowser ? new SpiderService(configManager) : { onMessageReceived: () => {}, spider: {} };
  const deviceIdStorage = isBrowser ? new BrowserDeviceIDStorage(localStorage) : stubDIDStorage;

  if (isBrowser && configManager.has('SENTRY_DSN')) {
    init({
      debug: configManager.get('SENTRY_DEBUG', false),
      dsn: configManager.getOrThrow('SENTRY_DSN'),
      release: configManager.get('BUILD_ID', ''),
      environment: configManager.get('APP_ENV', 'production'),
    });
  }
  container.bind({ provide: HISTORY_API, useValue: isBrowser ? createBrowserHistory() : createMemoryHistory() });
  container.bind({
    provide: ROUTER,
    useValue: isBrowser
      ? new BrowserRouter(configManager, container.get(HISTORY_API))
      : new StaticRouter(new URL(configManager.get('CLIENT_WEB_ORIGIN', '') as string)), // TODO: set right URL
  });
  container.bind({ provide: EVENT_BUS_TOKEN, useValue: eventBus });
  container.bind({ provide: CONFIG_MANAGER_TOKEN, useValue: configManager });
  container.bind({ provide: SPIDER_SERVICE_TOKEN, useValue: spiderService });
  container.bind({
    provide: tokens.DEVICE_ID_GENERATOR,
    useFactory: () => ('crypto' in window && typeof crypto.randomUUID !== 'undefined' ? uuidGenerator : fingerprint),
  });
  container.bind({ provide: tokens.DEVICE_ID_STORAGE, useValue: deviceIdStorage });
  container.bind({
    provide: DEVICE_IDENTIFIER,
    useValue: () => getDeviceId(container.get(tokens.DEVICE_ID_STORAGE), container.get(tokens.DEVICE_ID_GENERATOR)),
  });
  container.bind({
    provide: SELECTION_STORAGE,
    useValue: isBrowser
      ? new CachedJSONStorage<SelectionWithPriority>(window.sessionStorage, 'clientSelection')
      : new MemoryStorage<SelectionWithPriority>(),
  });
  container.bind({ provide: SELECTION_SERVICE, useValue: new ClientSelectionService(container.get(SELECTION_STORAGE)) });
  container.bind({ provide: CLIENT_SELECTION_GETTER, useValue: () => Promise.resolve(container.get(SELECTION_SERVICE).getSelections()) });
  container.bind({ provide: GET_CLIENT_SELECTIONS, useValue: () => Promise.resolve(container.get(SELECTION_SERVICE).getSelections()) });

  const store = configureStore({
    preloadedState: redux.preloadedState as RootState,
    persistConfig: redux.persistConfig,
    configManager: container.get(CONFIG_MANAGER_TOKEN),
  });
  container.bind({ provide: STORE, useValue: store });

  container.bind({ provide: MODAL_MANAGER, useValue: new RefModalManager(eventBus as any) });

  eventBus.subscribe(spiderService.onMessageReceived.bind(spiderService));
  if (configManager.get('DEBUG')) {
    isBrowser && eventBus.subscribe((e) => console.debug('[📧]', e[Symbol.toPrimitive] ? e[Symbol.toPrimitive]('default') : e));
    isBrowser && (window['eventBus'] = eventBus);
    isBrowser && (window['spider'] = spiderService.spider);
  }

  registerServiceWorker(configManager);

  console.groupEnd();
  return container;
}
